import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem("access") || localStorage.getItem("token");
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Format coordinate to ensure it doesn't exceed 10 digits total (same logic as attendance.service)
const formatCoordinate = (coord: number): number => {
  return parseFloat(coord.toFixed(6));
};

interface OrderStatistics {
  total: number;
  ready: number;
  out_for_delivery: number;
  delivered: number;
  failed: number;
}

interface TodayOrdersResponse {
  orders: any[];
  statistics: OrderStatistics;
}

/**
 * Get all today's orders with statistics
 * GET /delivery/orders/today/
 */
export const fetchTodaysDeliveries = async (): Promise<TodayOrdersResponse> => {
  const response = await axios.get(`${BASE_URL}/delivery/orders/today/`, {
    headers: getAuthHeaders(),
  });

  // Handle nested response structure (response.data.data or response.data)
  const data = response.data?.data || response.data;
  
  // Ensure we return the correct structure
  return {
    orders: data?.orders || [],
    statistics: data?.statistics || {
      total: 0,
      ready: 0,
      out_for_delivery: 0,
      delivered: 0,
      failed: 0,
    },
  };
};

/**
 * Get pending and rejected orders (for backward compatibility)
 * Filters today's orders to get only pending and rejected
 */
export const fetchPendingRejectedOrders = async () => {
  const data = await fetchTodaysDeliveries();
  
  // Filter orders that are pending or rejected
  // Note: Based on new API, orders might have status: ready, out_for_delivery, delivered, failed
  // We'll treat "ready" as pending and "failed" as rejected
  const pendingRejected = data.orders.filter((order: any) => 
    order.status === "ready" || order.status === "failed"
  );

  return pendingRejected;
};

/**
 * Pick up order from store (ready → out_for_delivery)
 * POST /api/v1/delivery/orders/<id>/pickup/
 * @param orderId - Order ID
 */
export const pickupOrder = async (orderId: string | number): Promise<any> => {
  const response = await axios.post(
    `${BASE_URL}/delivery/orders/${orderId}/pickup/`,
    {},
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Update order status (delivered, failed, or cancelled)
 * POST /delivery/orders/<id>/update-status/
 *
 * Payload by status:
 *   delivered:   { status, latitude, longitude, delivery_notes?, override_location? }
 *   failed:     { status, failure_reason }
 *   cancelled:  { status, cancellation_reason }
 */
export const updateOrderStatus = async (
  orderId: string | number,
  status: "delivered" | "failed" | "cancelled",
  options?: {
    latitude?: number;
    longitude?: number;
    deliveryNotes?: string;
    failureReason?: string;
    cancellationReason?: string;
    overrideLocation?: boolean;
  }
): Promise<any> => {
  const payload: any = { status };

  if (status === "delivered") {
    if (!options?.latitude || !options?.longitude) {
      throw new Error("Latitude and longitude are required for delivered status");
    }
    payload.latitude = formatCoordinate(options.latitude);
    payload.longitude = formatCoordinate(options.longitude);
    if (options.deliveryNotes) payload.delivery_notes = options.deliveryNotes;
    if (options.overrideLocation === true) payload.override_location = true;
  } else if (status === "failed") {
    if (!options?.failureReason) {
      throw new Error("Failure reason is required for failed status");
    }
    payload.failure_reason = options.failureReason;
  } else if (status === "cancelled") {
    if (!options?.cancellationReason) {
      throw new Error("Cancellation reason is required for cancelled status");
    }
    payload.cancellation_reason = options.cancellationReason;
  }

  const response = await axios.post(
    `${BASE_URL}/delivery/orders/${orderId}/update-status/`,
    payload,
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Mark order as delivered (convenience wrapper)
 * @param orderId - Order ID
 * @param latitude - Delivery latitude
 * @param longitude - Delivery longitude
 * @param deliveryNotes - Optional delivery notes
 * @param overrideLocation - If true, bypasses 200m location check (for when user confirms override)
 */
export const markOrderAsDelivered = async (
  orderId: string | number,
  latitude: number,
  longitude: number,
  deliveryNotes?: string,
  overrideLocation?: boolean
): Promise<any> => {
  return updateOrderStatus(orderId, "delivered", {
    latitude,
    longitude,
    deliveryNotes,
    overrideLocation,
  });
};

/**
 * Reject order (convenience wrapper for failed status)
 * @param orderId - Order ID
 * @param reason - Rejection reason
 */
export const rejectOrder = async (
  orderId: string | number,
  reason: string
): Promise<any> => {
  return updateOrderStatus(orderId, "failed", {
    failureReason: reason,
  });
};

/**
 * Cancel order (convenience wrapper for cancelled status)
 * e.g. customer refused delivery
 * @param orderId - Order ID
 * @param reason - Cancellation reason
 */
export const cancelOrder = async (
  orderId: string | number,
  reason: string
): Promise<any> => {
  return updateOrderStatus(orderId, "cancelled", {
    cancellationReason: reason,
  });
};

/**
 * Mark order as undelivered / failed.
 * POST /delivery/orders/<order_ref>/mark-undelivered/
 * Body: { failure_reason } — order_ref may be id or order_number (e.g. GP-20260515-EE2DD).
 */
export const markOrderAsUndelivered = async (
  orderRef: string | number,
  failureReason = "Marked as undelivered by delivery partner"
): Promise<any> => {
  const response = await axios.post(
    `${BASE_URL}/delivery/orders/${orderRef}/mark-undelivered/`,
    { failure_reason: failureReason },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get next delivery order / stop in sequence
 * GET /delivery/next-order/
 * Delivery Partner: Get next delivery stop.
 */
export const getNextOrder = async (): Promise<any> => {
  const response = await axios.get(`${BASE_URL}/delivery/next-order/`, {
    headers: getAuthHeaders(),
  });

  return response.data;
};

/** @deprecated Use getNextOrder - kept for backward compatibility */
export const getNextStop = getNextOrder;

